// // components/ui/loading-indicator.tsx
// v2

// components/ui/loading-indicator.tsx
"use client";

import { Loader2 } from "lucide-react";
import * as React from "react";

import { useLoadingStore } from "@/app/shared/store/loadingStore";
import { cn } from "@/lib/utils";

interface LoadingIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Override the global loading state
   */
  loading?: boolean;
  
  /**
   * Named loading state to check
   */
  loadingName?: string;
  
  /**
   * Size of the spinner (small, medium, large)
   */
  size?: "sm" | "md" | "lg";
  
  /**
   * Whether to show the loading indicator as an overlay
   */
  overlay?: boolean;
  
  /**
   * Custom loading text
   */
  text?: string;
}

/**
 * Loading indicator component that can use:
 * 1. A local loading prop
 * 2. A named loading state from the store
 * 3. The global loading state
 */
export function LoadingIndicator({
  loading,
  loadingName,
  size = "md",
  overlay = false,
  text,
  className,
  ...props
}: LoadingIndicatorProps) {
  const { isLoading: globalLoading, isAnyLoading, getNamedLoadingState } = useLoadingStore();
  
  // Determine if we should show loading
  const isLoading = React.useMemo(() => {
    // Local prop takes precedence
    if (loading !== undefined) return loading;
    
    // Check named loading state if provided
    if (loadingName) return getNamedLoadingState(loadingName);
    
    // Fall back to global loading state
    return globalLoading || isAnyLoading();
  }, [loading, loadingName, globalLoading, isAnyLoading, getNamedLoadingState]);
  
  // Don't render anything if not loading
  if (!isLoading) return null;
  
  // Size classes
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };
  
  // Text classes
  const textClasses = {
    sm: "text-xs mt-1",
    md: "text-sm mt-2",
    lg: "text-base mt-3",
  };
  
  // Overlay classes
  const containerClasses = overlay
    ? "fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
    : "flex flex-col items-center justify-center";
  
  return (
    <div className={cn(containerClasses, className)} {...props}>
      <div className="flex flex-col items-center justify-center bg-background/80 p-6 rounded-lg shadow-lg">
        <Loader2 className={cn("animate-spin text-primary", sizeClasses[size])} />
        {text && <p className={cn("text-center text-foreground", textClasses[size])}>{text}</p>}
      </div>
    </div>
  );
}

// v1
// "use client";

// import * as React from "react";
// import { useLoadingStore } from "@/app/shared/store/loadingStore";
// import { cn } from "@/lib/utils";

// interface LoadingIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
//   /**
//    * Override the global loading state
//    */
//   loading?: boolean;
  
//   /**
//    * Size of the spinner (small, medium, large)
//    */
//   size?: "sm" | "md" | "lg";
  
//   /**
//    * Whether to show the loading indicator as an overlay
//    */
//   overlay?: boolean;
  
//   /**
//    * Custom loading text
//    */
//   text?: string;
// }

// /**
//  * Loading indicator that uses the global loading state by default
//  * Can be overridden with a local loading state prop
//  */
// export function LoadingIndicator({
//   loading,
//   size = "md",
//   overlay = false,
//   text,
//   className,
//   ...props
// }: LoadingIndicatorProps) {
//   // Use the global loading state if no local state is provided
//   const { isLoading: globalLoading, isAnyLoading } = useLoadingStore();
//   const isLoading = loading !== undefined ? loading : (globalLoading || isAnyLoading());
  
//   // Don't render anything if not loading
//   if (!isLoading) return null;
  
//   // Size classes
//   const sizeClasses = {
//     sm: "w-4 h-4 border-2",
//     md: "w-8 h-8 border-3",
//     lg: "w-12 h-12 border-4",
//   };
  
//   // Text classes
//   const textClasses = {
//     sm: "text-xs mt-1",
//     md: "text-sm mt-2",
//     lg: "text-base mt-3",
//   };
  
//   // Overlay classes
//   const containerClasses = overlay
//     ? "fixed inset-0 flex items-center justify-center bg-black/30 z-50"
//     : "flex flex-col items-center justify-center";
  
//   return (
//     <div className={cn(containerClasses, className)} {...props}>
//       <div className="flex flex-col items-center">
//         <div
//           className={cn(
//             "animate-spin rounded-full border-t-transparent border-solid",
//             "border-primary",
//             sizeClasses[size]
//           )}
//         />
//         {text && <p className={cn("text-center text-foreground", textClasses[size])}>{text}</p>}
//       </div>
//     </div>
//   );
// } 